// @owners { team: patients }
import { SPACING } from '@alto/design-library-tokens';
import {
  ActionSheetContext,
  ActionSheetV2,
  Button,
  Description,
  InlineAlert,
  InputRadio,
  InputRadioGroup,
  LgPadding,
  MdSpacing,
  Toast,
  ToastContext,
  XsSpacing,
} from '@alto/design-system';
import {
  type AttestationRequestItem,
  type AttestationRequestOption,
} from '@alto/scriptdash/alto/patient_app/attestations/v1/attestation_requests_endpoint';
import React, { useCallback, useContext, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { TermsWebView } from '../../shared/components/TermsWebView';
import { isRebateProgram } from '../helpers/isRebateProgram';
import { useCreateAttestation } from '../queries/queries';

type Props = {
  attestationRequest: AttestationRequestItem;
  onDismiss: () => void;
  onSuccess: () => void;
};

const REBATE_SUCCESS_TOAST = 'Discount applied';
const COPAY_SUCCESS_TOAST = 'Copay card added and saved to your profile';
const SUBMIT_ERROR_TITLE = 'Could not record attestation';
const SUBMIT_ERROR_FALLBACK = 'Please try again or contact support.';

const styles = StyleSheet.create({
  contentWrapper: { flexGrow: 0 },
  scrollContentCompact: { flexGrow: 0 },
});

const sortOptionsByDisplayOrder = (options: AttestationRequestOption[]) =>
  [...options].sort((a, b) => a.display_order - b.display_order);

export const AttestationActionSheet = ({ attestationRequest, onDismiss, onSuccess }: Props) => {
  const { goToPreviousActionSheet } = useContext(ActionSheetContext);
  const { addToast } = useContext(ToastContext);
  const { mutateAsync, isPending } = useCreateAttestation();
  const { trackEvent } = useAnalytics();

  const { program_config: programConfig, statement } = attestationRequest;
  const sortedOptions = sortOptionsByDisplayOrder(statement.options);

  const [selectedOptionId, setSelectedOptionId] = useState('');
  const [submitError, setSubmitError] = useState('');

  const trackAttestationDismissed = useCallback(() => {
    trackEvent({
      event: EVENTS.ATTESTATION_DISMISSED,
      params: {
        billable_type: programConfig.billable_type,
        attestation_type: statement.unique_identifier,
      },
    });
  }, [programConfig.billable_type, statement.unique_identifier, trackEvent]);

  const handleSelectOption = useCallback((optionId: string) => {
    setSelectedOptionId(optionId);
    setSubmitError('');
  }, []);

  const handleDismiss = useCallback(() => {
    trackAttestationDismissed();
    onDismiss();
    goToPreviousActionSheet();
  }, [goToPreviousActionSheet, onDismiss, trackAttestationDismissed]);

  const handleBackFromConsent = useCallback(() => {
    trackAttestationDismissed();
    onDismiss();
  }, [onDismiss, trackAttestationDismissed]);

  const handleConfirm = async () => {
    if (!selectedOptionId) {
      return;
    }

    const selectedOption = sortedOptions.find((option) => option.id.toString() === selectedOptionId);
    if (!selectedOption) {
      return;
    }

    setSubmitError('');

    try {
      await mutateAsync({
        attestation_type: statement.unique_identifier,
        election_name: selectedOption.election_name,
      });

      const successMessage = isRebateProgram(programConfig.billable_type) ? REBATE_SUCCESS_TOAST : COPAY_SUCCESS_TOAST;
      addToast(<Toast variant="success">{successMessage}</Toast>);
      onSuccess();
      goToPreviousActionSheet();
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : SUBMIT_ERROR_FALLBACK);
    }
  };

  return (
    <ActionSheetV2
      analyticsName="attestation_action_sheet"
      contentContainerStyle={styles.scrollContentCompact}
      description={programConfig.offer_description ?? undefined}
      onBack={handleBackFromConsent}
      footnote={
        submitError ? (
          <>
            <InlineAlert type="error">
              <Description fontFamily="bold">{SUBMIT_ERROR_TITLE}</Description>
              <XsSpacing />
              <Description>{submitError}</Description>
            </InlineAlert>
            <MdSpacing />
          </>
        ) : undefined
      }
      fullscreen
      title={programConfig.terms_title ?? undefined}
      buttons={[
        <Button
          disabled={!selectedOptionId || isPending}
          key="attestation-confirm-button"
          label={programConfig.patient_cta_text}
          loading={isPending}
          loadingLabel="Submitting"
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onPress={handleConfirm}
        />,
        <Button
          key="attestation-cancel-button"
          label="Cancel"
          onPress={handleDismiss}
          type="tertiary"
        />,
      ]}
    >
      <View
        style={styles.contentWrapper}
        testID="attestation-action-sheet-content"
      >
        <LgPadding topPadding={SPACING.STATIC.NONE}>
          <TermsWebView htmlContent={statement.terms} />
          <MdSpacing />
          <InputRadioGroup onValueChange={handleSelectOption}>
            {sortedOptions.map((option) => (
              <InputRadio
                key={option.id}
                label={option.description}
                value={option.id.toString()}
              />
            ))}
          </InputRadioGroup>
        </LgPadding>
      </View>
    </ActionSheetV2>
  );
};
